import { createContext, Fragment, use, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Bot, Brain, ChevronRight, ChevronUp, FileDiff, ChevronsDownUp, ChevronsUpDown, Eye, Globe, Hammer, Search, Sparkles, SquarePen, SquareTerminal, Wrench, X, type LucideIcon } from 'lucide-react';
import type { T3ActivityItem, T3ActivityResult, T3ActivitySubagent } from '@shared/agent-protocol';
import type { RunThreadSummary } from '@shared/types/factory';
import { LoadError } from '@/components/LoadError';
import { LoadingState } from '@/components/LoadingState';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { useLoader } from '@/hooks/use-loader';
import { useNow } from '@/hooks/use-now';
import { Spinner } from '@/components/ui/spinner';
import { codeOf, describeApiError } from '@/lib/api-errors';
import { folderGoneOf, type FolderGone } from '@shared/folder-gone';
import { formatRelative } from '@/lib/formatters';
import { modelName } from '@/lib/usage-format';
import type { ApiResponse } from '@/lib/api-contract';
import { getRunActivity, getRunFileDiff, getRunImage, type RunActivityQuery } from '@/lib/runs-api';
import { AgentPictureContext, type PictureReader } from './agent-picture';
import { MACHINE_READ_TIME_LIMIT_MS, limitApiCall } from '@/lib/time-limit';
import { cn } from '@/lib/utils';
import { activityStatusMeta, changedFiles, type ChangedFiles, commandText, isFileItem, isLiveItem, isLiveStatus, isOneLineItem, isWorkItem, itemLabel, mergeActivity, nextAfter, formatElapsed, threadRows, threadTurns, toolLine, toolName, toolRunSummary, type ThreadRow, type ThreadTurn, toolRunKind, isFailedItem, FOLD_LINE, latestThought, pairSubagents, threadWorking, type ThreadWorking, type ToolRunKind } from './agent-activity';
import { CopyTextButton, MessageActions, UserMessageBubble } from './ChatMessageParts';
import { AgentMessageText } from './AgentMessageText';
import { ChangedFilesButton, type DiffReader } from './ChangedFilesButton';

/** New items while the run is going, as the task asked: every 5 s while the feed is open. */
const ACTIVITY_POLL_MS = 5000;
/** SF-T-266: a waiting run's thread, for a turn started elsewhere (T3 Code). */
const WAITING_POLL_MS = 20_000;

/** Reads a thread's activity from its machine: a run's by default; a chat session passes its own (T-51). */
export type ActivityReader = (query: RunActivityQuery, opts: { signal?: AbortSignal }) => Promise<ApiResponse<{ activity: T3ActivityResult }>>;

export interface AgentActivityFeedProps {
  /** The run (or, with `read`, the chat session) whose agent this is. */
  runId: string;
  /** Reads the activity instead of the run's: a chat session's (T-51). Keep it stable (useCallback). */
  read?: ActivityReader;
  /** The project's slug or id, for the request (the task pop-up opens outside the project's routes). */
  project?: string;
  /** One of the run thread's subagents instead of the thread itself. */
  subagentThreadId?: string;
  /** Poll for new items (the run is still going). */
  live: boolean;
  /**
   * SF-T-266: a run that's waiting (a parked live run, `live` false): still look in now and then, and count as live
   * while its thread shows a turn going (someone wrote to it in T3 Code).
   */
  followThread?: boolean;
  className?: string;
  /** Preview only (the /dev/ui gallery): show this instead of asking the machine. */
  preview?: T3ActivityResult;
  /**
   * SF-T-209: `thread` reads like the conversation (the task's Agent tab): oldest first, the agent's messages in
   * full as Markdown with the factory's blocks as cards, the prompts it was sent set apart. `feed` (the run page):
   * newest first, each item a short preview.
   */
  layout?: 'feed' | 'thread';
  /** SF-T-209: after each read of the thread: the error's code, or null when it was read (the Agent tab goes read-only on some). */
  onRead?: (errorCode: string | null) => void;
  /**
   * SF-T-222: a file's diff for the changed-files popup. By default a run's, from its worktree; null for none (a chat
   * session's thread, which has no run).
   */
  readDiff?: DiffReader | null;
  /** SF-T-266: where the pictures in the agent's messages are read (null: shown as links). Defaults to the run's worktree; preview only otherwise. */
  readPicture?: PictureReader | null;
  /** SF-T-222: Summarize thread (thread layout): a light AI call's status update, shown inline, not saved on the task. */
  summarize?: () => Promise<ApiResponse<{ summary: RunThreadSummary }>>;
  /** The header row's (status, the changed-files button, Summarize, Collapse all): the Agent tab pins it with `sticky`. */
  headerClassName?: string;
  /**
   * SF-T-266: the turn the agent is in after each read (null when it isn't working): for a Working pill outside the
   * thread, and for the message box to queue (not Send) while it works.
   */
  onWorking?: (working: ThreadWorking | null) => void;
  /** SF-T-266: a Working pill outside the thread says it (a phone, full screen): the thread leaves out its own Working line. */
  workingShownOutside?: boolean;
  /**
   * SF-T-273 (thread layout): what goes under the thread when it's stuck on its missing worktree folder (folderGoneOf):
   * the Agent tab's Continue in a new worktree.
   */
  renderFolderGone?: (gone: FolderGone) => ReactNode;
  /** SF-T-273: told after each read whether the thread is stuck on its missing folder (with renderFolderGone), for the message box. */
  onFolderGone?: (gone: FolderGone | null) => void;
}

interface FeedState {
  feed: T3ActivityResult | undefined;
  /** The last read's error (describeApiError); the last good feed stays on screen. */
  error: string | null;
}

/**
 * A run's coding agent at work (T-27), read live from its machine through the hub and never stored:
 * the T3 thread's latest items, newest first (thinking, commands, tool calls, messages), each a short
 * preview that opens its full text; Show earlier pages back; the thread's subagents, each opening its
 * own feed. Polls every 5 s while open and `live`.
 */
export function AgentActivityFeed({
  runId,
  read,
  project,
  subagentThreadId,
  live: runLive,
  followThread = false,
  className,
  preview,
  layout = 'feed',
  onRead,
  readDiff,
  readPicture,
  summarize,
  headerClassName,
  onWorking,
  workingShownOutside = false,
  renderFolderGone,
  onFolderGone,
}: AgentActivityFeedProps) {
  const thread = layout === 'thread';
  // The newest callback for the loader's reads, without making each render's a new loader.
  const onReadRef = useRef(onRead);
  useLayoutEffect(() => {
    onReadRef.current = onRead;
  });
  const reader = useReader(runId, project, read);
  const diffReader = useMemo<DiffReader | undefined>(() => {
    if (readDiff !== undefined) return readDiff ?? undefined;
    if (read || preview) return undefined;
    return (path, opts) => limitApiCall((signal) => getRunFileDiff(runId, path, { signal, project }), MACHINE_READ_TIME_LIMIT_MS, opts.signal);
  }, [readDiff, read, preview, runId, project]);
  const pictures = useMemo(() => {
    if (readPicture !== undefined) return readPicture ? { read: readPicture, scope: `preview:${runId}` } : null;
    if (read || preview) return null;
    const reader: PictureReader = (path, opts) => limitApiCall((signal) => getRunImage(runId, path, { signal, project }), MACHINE_READ_TIME_LIMIT_MS, opts.signal);
    return { read: reader, scope: runId };
  }, [readPicture, read, preview, runId, project]);
  const feedRef = useRef<T3ActivityResult | undefined>(undefined);
  const [threadBusy, setThreadBusy] = useState(false);
  const [earlier, setEarlier] = useState<{ loading: boolean; error: string | null }>({ loading: false, error: null });
  // SF-T-266: Expand all opens every finished turn; a turn opened or closed by hand keeps that until Expand or Collapse all.
  const [expandAll, setExpandAll] = useState(false);
  const [openTurns, setOpenTurns] = useState<Record<number, boolean>>({});
  // SF-T-222: the summaries asked for here, each after the newest item when it was asked.
  const [summaries, setSummaries] = useState<{ after: number; summary: RunThreadSummary }[]>([]);
  const [summarizing, setSummarizing] = useState<{ loading: boolean; error: string | null }>({ loading: false, error: null });
  const loader = useLoader<FeedState>(
    async (signal) => {
      const after = feedRef.current ? nextAfter(feedRef.current.items) : null;
      const res = await reader({ subagent: subagentThreadId, after }, { signal });
      if (!signal?.aborted) onReadRef.current?.(res.data ? null : (codeOf(res) ?? 'ERROR'));
      if (!res.data) return { feed: feedRef.current, error: describeApiError(res) };
      // Merged into what's there now: "Show earlier" may have landed while this read ran.
      feedRef.current = mergeActivity(feedRef.current, res.data.activity, 'newer');
      return { feed: feedRef.current, error: null };
    },
    [reader, subagentThreadId],
    {
      enabled: !preview,
      pollMs: runLive || threadBusy ? ACTIVITY_POLL_MS : followThread ? WAITING_POLL_MS : undefined,
      pollWhile: () => runLive || followThread,
    }
  );
  const feed = preview ?? loader.data?.feed;
  // SF-T-266: a waiting run whose thread is at work anyway (a turn started in T3 Code) shows and polls as live.
  const busyNow = followThread && !runLive && !!feed && isLiveStatus(feed.thread.status);
  if (busyNow !== threadBusy) setThreadBusy(busyNow);
  const live = runLive || busyNow;
  // SF-T-266: the turn going now, told to a Working pill outside the thread (when the read changes it).
  const workingNow = feed && thread ? threadWorking(feed, live) : null;
  const workingKey = workingNow ? `${workingNow.since}|${workingNow.agents.running}/${workingNow.agents.total}` : '';
  const onWorkingRef = useRef(onWorking);
  const lastWorking = useRef(workingNow);
  useLayoutEffect(() => {
    onWorkingRef.current = onWorking;
    lastWorking.current = workingNow;
  });
  useEffect(() => {
    onWorkingRef.current?.(lastWorking.current);
  }, [workingKey]);
  const error = preview ? null : (loader.data?.error ?? null);
  const changes = useMemo(() => changedFiles(feed?.items ?? []), [feed?.items]);
  // SF-T-273: the thread is stuck on its missing worktree folder.
  const gone = useMemo(() => (thread && renderFolderGone && feed ? folderGoneOf(feed.items, feed.thread.status) : null), [thread, renderFolderGone, feed]);
  const goneKey = gone ? `${gone.errorItemId}|${gone.message?.itemId ?? ''}` : '';
  const onFolderGoneRef = useRef(onFolderGone);
  const lastGone = useRef(gone);
  useLayoutEffect(() => {
    onFolderGoneRef.current = onFolderGone;
    lastGone.current = gone;
  });
  useEffect(() => {
    onFolderGoneRef.current?.(lastGone.current);
  }, [goneKey]);

  const showEarlier = async () => {
    const first = feedRef.current?.items[0];
    if (!first) return;
    setEarlier({ loading: true, error: null });
    const res = await reader({ subagent: subagentThreadId, before: first.position }, {});
    if (!res.data) {
      setEarlier({ loading: false, error: describeApiError(res) });
      return;
    }
    feedRef.current = mergeActivity(feedRef.current, res.data.activity, 'earlier');
    loader.setData({ feed: feedRef.current, error: null });
    setEarlier({ loading: false, error: null });
  };

  const askSummary = async () => {
    if (!summarize) return;
    const after = feed?.items.at(-1)?.position ?? -1;
    setSummarizing({ loading: true, error: null });
    const res = await summarize();
    if (!res.data) {
      setSummarizing({ loading: false, error: describeApiError(res) });
      return;
    }
    const summary = res.data.summary;
    setSummaries((list) => [...list, { after, summary }]);
    setSummarizing({ loading: false, error: null });
  };

  // SF-T-266: each turn's changed files (its checkpoint and file changes, which the thread doesn't show as rows), by its prompt.
  const turnFiles = useMemo(() => {
    const out = new Map<number, ChangedFiles>();
    if (!thread || !feed) return out;
    let key = -1;
    let group: T3ActivityItem[] = [];
    const flush = () => {
      const changes = changedFiles(group);
      if (changes.files.length) out.set(key, changes);
    };
    for (const item of feed.items) {
      if (item.type === 'user_message') {
        flush();
        key = item.position;
        group = [];
      } else group.push(item);
    }
    flush();
    return out;
  }, [thread, feed]);
  if (!feed) {
    if (error) return <LoadError compact title="Couldn't read the agent's activity" error={error} onRetry={loader.reload} className={className} />;
    return <LoadingState inline label="Asking the machine…" className={cn('px-1 py-2', className)} />;
  }

  // SF-T-222: file changes and checkpoints add up in the changed-files button instead of taking rows.
  const shown = feed.items.filter((item) => !isFileItem(item));
  const items = thread ? shown : [...shown].reverse();
  // SF-T-266: in the thread, turns as t3code folds them (each finished one behind "Worked for 4m"); in the feed, items.
  const turns = thread ? threadTurns(items, live, feed.thread.status) : [];
  // A turn that was stopped or failed stays open, so you see where it ended (t3code's).
  const turnOpen = (turn: ThreadTurn) => openTurns[turn.key] ?? (expandAll || turn.ended !== 'done');
  // Each summary goes after the turn it was asked in (the feed has none).
  const summariesIn = (index: number) => {
    const at = turns[index]!.key;
    const next = turns[index + 1]?.key ?? Infinity;
    return summaries.filter((s) => s.after >= at && s.after < next);
  };
  const working = live && isLiveStatus(feed.thread.status);
  // SF-T-266: in the thread, a subagent shows as a card where it was started; the list above keeps the rest.
  const byItem = thread ? pairSubagents(shown, feed.subagents) : new Map<number, T3ActivitySubagent>();
  const carded = new Set(byItem.values());
  const listed = feed.subagents.filter((s) => !carded.has(s));
  const subagentScope: SubagentScope = { byItem, runId, read, project, live, preview };
  const leading = thread ? summaries.filter((s) => !items.length || s.after < items[0]!.position) : [];
  const dismiss = (summary: RunThreadSummary) => setSummaries((list) => list.filter((s) => s.summary !== summary));
  const earlierButton =
    feed.hasEarlier && !preview ? (
      <div className={cn('flex flex-wrap items-center gap-2', thread && 'justify-center')}>
        {/* SF-T-266: in the thread, as T3 Code's: a pill at the top, "Load earlier activity". */}
        <Button
          type="button"
          variant={thread ? 'secondary' : 'ghost'}
          size="xs"
          className={cn(thread && 'rounded-full px-3 pointer-coarse:h-9')}
          loading={earlier.loading}
          loadingText="Loading…"
          onClick={() => void showEarlier()}
        >
          {thread ? <ChevronUp aria-hidden /> : null}
          {thread ? 'Load earlier activity' : 'Show earlier'}
        </Button>
        {earlier.error ? <span className="text-xs text-danger-strong">{earlier.error}</span> : null}
      </div>
    ) : null;
  return (
    <SubagentContext value={subagentScope}>
    <AgentPictureContext value={pictures}>
    <div className={cn('space-y-2', className)}>
      <div className={cn('flex flex-wrap items-center gap-x-2 gap-y-1 text-2xs text-muted-foreground', headerClassName)}>
        <StatusBadge meta={activityStatusMeta(feed.thread.status) ?? { label: feed.thread.status, tone: 'neutral' }} size="sm" dot={live} />
        {/* SF-T-266: on a phone the thread's header is one row: the count goes, Summarize and Expand all are their icons. */}
        <span title={subagentThreadId ? undefined : 'Read live from the machine; not stored'} className={cn(thread && 'max-sm:hidden')}>
          {feed.items.length} of {feed.thread.itemCount} items{thread ? '' : ', newest first'}
        </span>
        <ChangedFilesButton changes={changes} readDiff={diffReader} />
        {thread ? (
          <div className="ml-auto flex items-center gap-0.5">
            {summarize ? (
              <Button
                type="button"
                variant="ghost"
                size="xs"
                className="text-2xs text-ai-strong"
                loading={summarizing.loading}
                loadingText="Summarizing…"
                onClick={() => void askSummary()}
              >
                <Sparkles aria-hidden />
                <span className="max-sm:sr-only">Summarize</span>
              </Button>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="xs"
              className="text-2xs text-muted-foreground"
              onClick={() => {
                setExpandAll(!expandAll);
                setOpenTurns({});
              }}
            >
              {expandAll ? <ChevronsDownUp aria-hidden /> : <ChevronsUpDown aria-hidden />}
              <span className="max-sm:sr-only">{expandAll ? 'Collapse all' : 'Expand all'}</span>
            </Button>
          </div>
        ) : null}
      </div>
      {summarizing.error ? <LoadError compact title="Couldn't summarize the thread" error={summarizing.error} onRetry={() => void askSummary()} /> : null}
      {error ? <LoadError compact title="Couldn't refresh the agent's activity" error={error} onRetry={loader.reload} /> : null}
      {listed.length ? <SubagentList subagents={listed} runId={runId} read={read} project={project} live={live} preview={preview} /> : null}
      {thread ? earlierButton : null}
      {items.length || leading.length ? (
        <ol className={cn(thread ? 'space-y-3' : 'space-y-1')} aria-label={subagentThreadId ? 'Subagent activity' : 'Agent activity'}>
          {leading.map((s) => (
            <ThreadSummaryRow key={s.summary.made_at} summary={s.summary} onDismiss={() => dismiss(s.summary)} />
          ))}
          {thread
            ? turns.map((turn, index) => (
                <Fragment key={turn.key}>
                  <TurnRows
                    turn={turn}
                    open={turnOpen(turn)}
                    onOpen={(open) => setOpenTurns((all) => ({ ...all, [turn.key]: open }))}
                    working={working && !turn.done && !workingShownOutside}
                    changes={turnFiles.get(turn.prompt?.position ?? -1) ?? null}
                    readDiff={diffReader}
                    live={live}
                    reader={reader}
                    subagentThreadId={subagentThreadId}
                    preview={!!preview}
                  />
                  {summariesIn(index).map((s) => (
                    <ThreadSummaryRow key={s.summary.made_at} summary={s.summary} onDismiss={() => dismiss(s.summary)} />
                  ))}
                </Fragment>
              ))
            : items.map((item) => <ActivityItemRow key={item.position} item={item} reader={reader} subagentThreadId={subagentThreadId} preview={!!preview} />)}
        </ol>
      ) : (
        <p className="text-xs text-muted-foreground">Nothing yet.</p>
      )}
      {gone && renderFolderGone ? renderFolderGone(gone) : null}
      {thread ? null : earlierButton}
    </div>
    </AgentPictureContext>
    </SubagentContext>
  );
}

/**
 * `read`, or the run's own activity; the same function while its inputs are, so the loader keys on it.
 * The machine answers it, so it gets the browser's own time limit (SF-T-153): a lost answer shows as an
 * error to retry, not a spinner that never stops.
 */
function useReader(runId: string, project: string | undefined, read: ActivityReader | undefined): ActivityReader {
  return useMemo<ActivityReader>(() => {
    const base: ActivityReader = read ?? ((query, opts) => getRunActivity(runId, query, { ...opts, project }));
    return (query, opts) => limitApiCall((signal) => base(query, { ...opts, signal }), MACHINE_READ_TIME_LIMIT_MS, opts.signal);
  }, [runId, project, read]);
}

/**
 * SF-T-266: what a thread's subagent card needs to open the subagent's own thread in place: the thread's list of
 * subagents and how to read them. Absent (Chat's turns), the card shows what the item says and doesn't open.
 */
interface SubagentScope {
  /** The subagent each subagent item started, by the item's position (pairSubagents). */
  byItem: ReadonlyMap<number, T3ActivitySubagent>;
  runId: string;
  read?: ActivityReader;
  project?: string;
  live: boolean;
  preview?: T3ActivityResult;
}
const SubagentContext = createContext<SubagentScope | null>(null);

/**
 * SF-T-266: a subagent in the thread where the agent started it, as t3code's mobile app shows one: a status dot, its
 * title, Working (or how it ended) and how long, and its latest step; it opens to its own thread, in place.
 */
function SubagentCard({ item }: { item: T3ActivityItem }) {
  const scope = use(SubagentContext);
  const [open, setOpen] = useState(false);
  const sub = scope?.byItem.get(item.position) ?? null;
  const status = sub?.status ?? item.status;
  const running = isLiveStatus(status);
  const meta = activityStatusMeta(status);
  const start = sub?.createdAt ? Date.parse(sub.createdAt) : NaN;
  const end = running ? NaN : sub?.updatedAt ? Date.parse(sub.updatedAt) : NaN;
  const now = useNow(1000, running && !Number.isNaN(start));
  const took = Number.isNaN(start) ? null : formatElapsed((Number.isNaN(end) ? Math.max(now, start) : end) - start);
  const latest = (item.text ?? '').split('\n').find((l) => l.trim());
  const canOpen = !!(scope && sub);
  const head = (
    <>
      <span className={cn('size-2 shrink-0 rounded-full', running ? 'animate-pulse bg-info-strong' : 'bg-muted-foreground/50')} aria-hidden />
      <Bot className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0 truncate text-sm font-medium">{sub?.title ?? item.title ?? 'Subagent'}</span>
      {meta ? <StatusBadge meta={running ? { label: 'Working', tone: 'info' } : meta} size="sm" className="shrink-0" /> : null}
      <span className="ml-auto shrink-0 text-2xs text-muted-foreground tabular-nums">
        {[took, sub ? `${sub.itemCount} items` : null].filter(Boolean).join(' · ')}
      </span>
      {canOpen ? <FoldChevron open={open} /> : null}
    </>
  );
  return (
    <li className="space-y-2 rounded-lg border bg-card px-3 py-2">
      {canOpen ? (
        <button type="button" className="flex min-h-6 w-full min-w-0 items-center gap-2 text-left pointer-coarse:min-h-9" aria-expanded={open} onClick={() => setOpen(!open)}>
          {head}
        </button>
      ) : (
        <div className="flex min-w-0 items-center gap-2">{head}</div>
      )}
      {/* SF-T-266: its model, as T3 Code's card (from agents that send it), then its latest step. */}
      {sub?.model ? <p className="truncate pl-4 text-2xs text-muted-foreground">{modelName(sub.model)}</p> : null}
      {latest ? <p className="truncate pl-4 text-xs text-muted-foreground">{latest}</p> : null}
      {open && scope && sub ? (
        <AgentActivityFeed
          runId={scope.runId}
          read={scope.read}
          project={scope.project}
          subagentThreadId={sub.threadId}
          live={scope.live && running}
          layout="thread"
          className="border-t pt-2"
          preview={scope.preview ? { ...scope.preview, subagents: [] } : undefined}
        />
      ) : null}
    </li>
  );
}

/** The thread's subagents, each opening its own feed. */
function SubagentList({
  subagents,
  runId,
  read,
  project,
  live,
  preview,
}: {
  subagents: T3ActivitySubagent[];
  runId: string;
  read?: ActivityReader;
  project?: string;
  live: boolean;
  preview?: T3ActivityResult;
}) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <ul className="space-y-0.5 rounded-md bg-muted/30" aria-label="Subagents">
      {subagents.map((s) => {
        const isOpen = open === s.threadId;
        const meta = activityStatusMeta(s.status);
        return (
          <li key={s.threadId} className="space-y-2 px-2 py-1.5">
            <button
              type="button"
              className="flex w-full items-center gap-2 text-left text-xs"
              aria-expanded={isOpen}
              onClick={() => setOpen(isOpen ? null : s.threadId)}
            >
              <ChevronRight className={cn('size-3.5 shrink-0 text-muted-foreground transition-transform', isOpen && 'rotate-90')} aria-hidden />
              <span className="text-2xs text-muted-foreground">Subagent</span>
              <span className="min-w-0 truncate font-medium">{s.title || s.threadId}</span>
              {meta ? <StatusBadge meta={meta} size="sm" /> : null}
              <span className="ml-auto shrink-0 text-2xs text-muted-foreground">
                {s.itemCount} items{s.updatedAt ? ` · ${formatRelative(s.updatedAt)}` : ''}
              </span>
            </button>
            {isOpen ? (
              <AgentActivityFeed
                runId={runId}
                read={read}
                project={project}
                subagentThreadId={s.threadId}
                live={live}
                className="pl-5"
                preview={preview ? { ...preview, subagents: [] } : undefined}
              />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/** One item read in full from the machine: its text from the start, and where the rest starts when it's longer still. */
interface FullText {
  text: string;
  nextOffset: number | null;
}

/**
 * One item. In the thread: the agent's messages in full, the prompts it was sent in a bubble on the right, its thinking
 * and other work each one line that opens on a click (SF-T-266). In the feed: commands and tool calls on one line, the
 * rest a short preview with Show all. Text the machine cut short is read in full when it's opened.
 */
function ActivityItemRow({
  item,
  reader,
  subagentThreadId,
  preview,
  thread = false,
}: {
  item: T3ActivityItem;
  reader: ActivityReader;
  subagentThreadId?: string;
  preview: boolean;
  /** SF-T-209: in the Agent tab's thread, messages show in full (the agent's as Markdown) and the rest as compact rows. */
  thread?: boolean;
}) {
  const message = thread && (item.type === 'assistant_message' || item.type === 'user_message');
  const agent = message && item.type === 'assistant_message';
  const [open, setOpen] = useState(agent);
  // Further pages of a very long text (Show more), for the text they were read for.
  const [more, setMore] = useState<{ forText: string | null; full: FullText; loading: boolean; error: string | null } | null>(null);

  // SF-T-266: in the thread, all the agent's work but its thinking is one line (thinking is ThoughtRow's).
  const oneLine = isOneLineItem(item.type) || (thread && isWorkItem(item) && item.type !== 'reasoning');
  const wantFull = open && item.textTruncated && !preview;
  const first = useLoader<{ full: FullText | null; error: string | null }>(
    async (signal) => {
      const res = await reader({ subagent: subagentThreadId, item: item.itemId }, { signal });
      const got = res.data?.activity.items[0];
      if (!got) return { full: null, error: res.data ? 'That item is gone.' : describeApiError(res) };
      return { full: { text: got.text ?? '', nextOffset: got.textTruncated ? got.nextTextOffset : null }, error: null };
    },
    [reader, subagentThreadId, item.itemId, item.text],
    { enabled: wantFull }
  );
  const full = wantFull ? (more?.forText === item.text ? more.full : (first.data?.full ?? null)) : null;
  const text = full?.text ?? item.text;
  const loading = wantFull && !first.data;
  const error = more?.forText === item.text && more.error ? more.error : wantFull ? (first.data?.error ?? null) : null;

  const readMore = async (from: FullText) => {
    if (from.nextOffset == null) return;
    const forText = item.text;
    setMore({ forText, full: from, loading: true, error: null });
    const res = await reader({ subagent: subagentThreadId, item: item.itemId, offset: from.nextOffset }, {});
    const got = res.data?.activity.items[0];
    if (!got) {
      setMore({ forText, full: from, loading: false, error: res.data ? 'That item is gone.' : describeApiError(res) });
      return;
    }
    setMore({ forText, full: { text: from.text + (got.text ?? ''), nextOffset: got.textTruncated ? got.nextTextOffset : null }, loading: false, error: null });
  };

  const meta = item.status && item.status !== 'completed' ? activityStatusMeta(item.status) : null;
  const time = item.updatedAt ? <span className="ml-auto shrink-0 text-3xs text-muted-foreground tabular-nums">{formatRelative(item.updatedAt)}</span> : null;

  if (oneLine) {
    return (
      <OneLineRow
        item={item}
        text={text ?? ''}
        heading={toolName(item) ?? (item.type === 'command_execution' ? null : itemLabel(item.type))}
        meta={meta}
        loading={loading}
        error={error}
        open={open}
        onOpen={setOpen}
        more={open && full?.nextOffset != null ? () => void readMore(full) : null}
        moreLoading={!!more?.loading}
      />
    );
  }

  // Cut short by the machine (read the rest), or only by the line clamp (just unclamp).
  const clampLines = message ? null : 4;
  const clamped = !open && ((item.textTruncated && !preview) || (clampLines != null && (item.text?.split('\n').length ?? 0) > clampLines));
  const buttons = (
    <div className="flex flex-wrap items-center gap-2 empty:hidden">
      {clamped ? (
        <Button type="button" variant="link" size="xs" className="h-auto px-0 text-2xs" onClick={() => setOpen(true)}>
          Show all
        </Button>
      ) : null}
      {open && full?.nextOffset != null ? (
        <Button type="button" variant="link" size="xs" className="h-auto px-0 text-2xs" loading={more?.loading} onClick={() => void readMore(full)}>
          Show more
        </Button>
      ) : null}
      {open && !agent ? (
        <Button type="button" variant="link" size="xs" className="h-auto px-0 text-2xs" onClick={() => setOpen(false)}>
          Show less
        </Button>
      ) : null}
      {loading ? <span className="text-2xs text-muted-foreground">Reading…</span> : null}
      {error ? <span className="text-2xs text-danger-strong">{error}</span> : null}
    </div>
  );

  if (message) {
    const status = meta ? <StatusBadge meta={meta} size="sm" dot={isLiveItem(item)} /> : null;
    // SF-T-266: as t3code lays them out: yours in a bubble on the right that folds when long; the agent's as plain
    // text with no label; the copy button and time under each, on hover.
    if (item.type === 'user_message') {
      return (
        <li className="group/msg flex flex-col items-end gap-0.5" data-sent>
          <UserMessageBubble text={text ?? ''} cutShort={item.textTruncated && !preview && !full} onExpand={setOpen}>
            <AgentMessageText text={text ?? ''} />
          </UserMessageBubble>
          {loading || error ? <div className="px-1">{buttons}</div> : null}
          <MessageActions align="end">
            {status}
            {item.updatedAt ? <span className="tabular-nums">{formatRelative(item.updatedAt)}</span> : null}
            {text ? <CopyTextButton text={text} label="Copy the message" /> : null}
          </MessageActions>
        </li>
      );
    }
    return (
      <li className="group/msg space-y-1 px-1">
        {text ? (
          <AgentMessageText text={text} />
        ) : null}
        {buttons}
        <MessageActions shown={!!status}>
          {text ? <CopyTextButton text={text} label="Copy the answer" /> : null}
          {status}
          {item.updatedAt ? <span className="tabular-nums">{formatRelative(item.updatedAt)}</span> : null}
        </MessageActions>
      </li>
    );
  }
  if (thread && item.type === 'subagent') return <SubagentCard item={item} />;
  if (thread && item.type === 'reasoning') {
    return <ThoughtRow text={text ?? ''} open={open} onOpen={setOpen} buttons={open ? buttons : null} live={isLiveItem(item)} />;
  }
  const heading = [itemLabel(item.type), toolName(item) ?? (item.title && item.title !== itemLabel(item.type) ? item.title : null)].filter(Boolean).join(' · ');
  return (
    <li className="space-y-0.5 px-1 py-1">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-3xs font-medium tracking-wide text-muted-foreground uppercase">{heading}</span>
        {meta ? <StatusBadge meta={meta} size="sm" dot={isLiveItem(item)} /> : null}
        {time}
      </div>
      {text ? (
        <p
          className={cn(
            'break-words whitespace-pre-wrap text-xs',
            item.type === 'reasoning' && 'text-muted-foreground italic',
            !open && 'line-clamp-4',
            open && 'max-h-96 overflow-y-auto'
          )}
        >
          {text}
        </p>
      ) : null}
      {buttons}
    </li>
  );
}

/**
 * A command or tool call (in the thread, any of the agent's work) on one line: its icon, what it is and its first line.
 * SF-T-266: a click opens its whole text under it, with a copy button, as t3code's work log does (it used to open on
 * hover, over what followed, which moved the scroll bars).
 */
function OneLineRow({
  item,
  text,
  heading,
  meta,
  loading,
  error,
  open,
  onOpen,
  more,
  moreLoading,
}: {
  item: T3ActivityItem;
  text: string;
  heading: string | null;
  meta: ReturnType<typeof activityStatusMeta>;
  loading: boolean;
  error: string | null;
  open: boolean;
  onOpen: (open: boolean) => void;
  /** Reads the next part of a very long text, when there's more. */
  more: (() => void) | null;
  moreLoading: boolean;
}) {
  const command = item.type === 'command_execution';
  const Icon = command ? SquareTerminal : item.type === 'subagent' ? Bot : Wrench;
  const line = (command ? text : toolLine(text)).split('\n')[0] || item.title;
  return (
    <li>
      <button type="button" className={cn(FOLD_LINE, 'px-1')} aria-expanded={open} onClick={() => onOpen(!open)}>
        <Icon className={cn('size-3.5 shrink-0', isFailedItem(item) && 'text-danger-strong/60')} aria-hidden />
        {heading ? <span className="shrink-0">{heading}</span> : null}
        <span className="min-w-0 truncate font-mono text-2xs leading-5">{line}</span>
        {meta ? <StatusBadge meta={meta} size="sm" dot={isLiveItem(item)} className="shrink-0" /> : null}
        <FoldChevron open={open} />
      </button>
      {open ? (
        <div className="mt-1 ml-5 space-y-1">
          <div className="relative rounded-md bg-muted/50 py-1.5 pr-8 pl-2">
            <pre className="max-h-80 overflow-y-auto font-mono text-2xs leading-5 break-words whitespace-pre-wrap">{text || item.title}</pre>
            <CopyTextButton text={command ? commandText(text) : text} label={command ? 'Copy the command' : 'Copy'} className="absolute top-1 right-1 size-5" />
          </div>
          {loading ? <p className="text-2xs text-muted-foreground">Reading the rest…</p> : null}
          {error ? <p className="text-2xs text-danger-strong">{error}</p> : null}
          {more ? (
            <Button type="button" variant="link" size="xs" className="h-auto px-0 text-2xs" loading={moreLoading} onClick={more}>
              Show more
            </Button>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

/**
 * SF-T-266: one turn of the thread, as t3code shows it: the prompt; once it's done, its work folded behind
 * "Worked for 4m" (opening to the work, itself folded into runs of a line each) and the agent's answer; while it
 * goes, "Working for 1m 12s" and everything as it comes.
 */
function TurnRows({
  turn,
  open,
  onOpen,
  working,
  changes = null,
  readDiff,
  live,
  reader,
  subagentThreadId,
  preview,
}: {
  turn: ThreadTurn;
  open: boolean;
  onOpen: (open: boolean) => void;
  working: boolean;
  /** SF-T-266: the files the turn changed, for a card under its answer. */
  changes?: ChangedFiles | null;
  readDiff?: DiffReader;
  live: boolean;
  reader: ActivityReader;
  subagentThreadId?: string;
  preview: boolean;
}) {
  const row = (r: ThreadRow) =>
    r.kind === 'item' ? (
      <ActivityItemRow key={r.item.position} item={r.item} reader={reader} subagentThreadId={subagentThreadId} preview={preview} thread />
    ) : (
      <ToolRunRow key={r.items[0]!.position} items={r.items} reader={reader} subagentThreadId={subagentThreadId} preview={preview} />
    );
  const prompt = turn.prompt ? <ActivityItemRow item={turn.prompt} reader={reader} subagentThreadId={subagentThreadId} preview={preview} thread /> : null;
  if (!turn.done) {
    const started = turn.prompt?.updatedAt ?? turn.items[0]?.updatedAt ?? null;
    // SF-T-266: what the agent is thinking now, as T3 Code shows it (under Working, or alone when the pill shows that).
    const thought = live ? latestThought(turn.items) : null;
    return (
      <>
        {prompt}
        {working ? <WorkingLine since={started} /> : null}
        {thought ? <ThoughtNow text={thought} /> : null}
        {threadRows(turn.items, live).map(row)}
      </>
    );
  }
  const after = turn.tookMs != null && turn.tookMs >= 1000 ? formatElapsed(turn.tookMs) : null;
  const took =
    turn.ended === 'stopped'
      ? after
        ? `Stopped after ${after}`
        : 'Stopped'
      : turn.ended === 'failed'
        ? after
          ? `Failed after ${after}`
          : 'Failed'
        : after
          ? `Worked for ${after}`
          : 'Worked';
  return (
    <>
      {prompt}
      {/* SF-T-266: messages steered into the turn stay in view, under its prompt. */}
      {turn.steers.map((item) => (
        <ActivityItemRow key={item.position} item={item} reader={reader} subagentThreadId={subagentThreadId} preview={preview} thread />
      ))}
      {turn.work.length ? (
        <li className="border-b border-border/60 px-1 pb-1.5">
          <button type="button" className={FOLD_LINE} aria-expanded={open} onClick={() => onOpen(!open)}>
            <span className={cn(turn.ended === 'failed' && 'text-danger-strong', turn.ended === 'stopped' && 'text-warning-strong')}>{took}</span>
            <span className="text-3xs">· {turn.work.length === 1 ? '1 step' : `${turn.work.length} steps`}</span>
            <FoldChevron open={open} />
          </button>
        </li>
      ) : null}
      {open ? threadRows(turn.work).map(row) : null}
      {turn.answer ? <ActivityItemRow item={turn.answer} reader={reader} subagentThreadId={subagentThreadId} preview={preview} thread /> : null}
      {/* SF-T-266: what the turn changed, under its answer, as T3 Code's card. */}
      {changes ? (
        <li className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-xs">
          <FileDiff className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
          <span>
            Changed {changes.files.length} {changes.files.length === 1 ? 'file' : 'files'}
          </span>
          <ChangedFilesButton changes={changes} readDiff={readDiff} className="ml-auto" />
        </li>
      ) : null}
    </>
  );
}

/** The chevron at a folded line's right edge: down when it opens, up once open. */
export function FoldChevron({ open }: { open: boolean }) {
  return <ChevronRight className={cn('ml-auto size-3.5 shrink-0 rotate-90 transition-transform', open && '-rotate-90')} aria-hidden />;
}

/** A folded run's icon by what it mostly did (t3code's), a hammer when it's a mix. */
const RUN_ICONS: Record<ToolRunKind, LucideIcon> = {
  thinking: Brain,
  command: SquareTerminal,
  read: Eye,
  edit: SquarePen,
  search: Search,
  web: Globe,
  subagent: Bot,
  tool: Wrench,
  mixed: Hammer,
};

/**
 * SF-T-266: a turn's work (thinking, commands, tool calls, the agent's messages on the way) as the Agent tab draws it:
 * runs folded to a line each that opens on a click. Chat shows it under a turn's "Worked for" and while a turn goes
 * (`live`: the run the agent is in stays open).
 */
export function ThreadWorkList({ items, reader, live = false, className }: { items: readonly T3ActivityItem[]; reader: ActivityReader; live?: boolean; className?: string }) {
  return (
    <ol className={cn('space-y-2', className)} aria-label="The agent's work">
      {threadRows(items, live).map((r) =>
        r.kind === 'item' ? (
          <ActivityItemRow key={r.item.position} item={r.item} reader={reader} preview={false} thread />
        ) : (
          <ToolRunRow key={r.items[0]!.position} items={r.items} reader={reader} preview={false} />
        )
      )}
    </ol>
  );
}

/** SF-T-266: the agent's latest thought while it works: a quiet line under Working, with a pulsing brain. */
export function ThoughtNow({ text, className }: { text: string; className?: string }) {
  return (
    <li className={cn('flex min-w-0 items-center gap-1.5 px-1 text-xs text-muted-foreground italic max-sm:text-sm', className)} aria-live="polite">
      <Brain className="size-3.5 shrink-0 animate-pulse not-italic" aria-hidden />
      <span className="min-w-0 truncate">{text}</span>
    </li>
  );
}

/** SF-T-266: "Working for 1m 12s", ticking, over the turn the agent is in. */
function WorkingLine({ since }: { since: string | null }) {
  const start = since ? Date.parse(since) : NaN;
  const now = useNow(1000, !Number.isNaN(start));
  return (
    <li className="flex items-center gap-2 border-b border-border/60 px-1 pb-1.5 text-xs text-muted-foreground tabular-nums">
      <Spinner size="xs" />
      {Number.isNaN(start) ? 'Working…' : `Working for ${formatElapsed(Math.max(now, start) - start)}`}
    </li>
  );
}

/**
 * SF-T-266: the agent's thinking on one quiet line (its first line, after a brain), as t3code shows it; it opens to
 * the whole thought.
 */
function ThoughtRow({ text, open, onOpen, buttons, live }: { text: string; open: boolean; onOpen: (open: boolean) => void; buttons: ReactNode; live: boolean }) {
  const line = text.split('\n').find((l) => l.trim())?.replace(/^[#*\s]+|\*+$/g, '') ?? '';
  return (
    <li className="px-1">
      <button type="button" className={FOLD_LINE} aria-expanded={open} onClick={() => onOpen(!open)}>
        <Brain className={cn('size-3.5 shrink-0', live && 'animate-pulse')} aria-hidden />
        <span className="min-w-0 truncate">{line || 'Thinking'}</span>
        <FoldChevron open={open} />
      </button>
      {open ? (
        <div className="mt-1 ml-5 space-y-1">
          <p className="max-h-96 overflow-y-auto text-xs break-words whitespace-pre-wrap text-muted-foreground italic">{text}</p>
          {buttons}
        </div>
      ) : null}
    </li>
  );
}

/**
 * SF-T-266: a run of commands and tool calls side by side, folded to one line that says what they did ("Ran 3
 * commands and read 2 files"), as t3code folds its work log; it opens to each on its own line.
 */
function ToolRunRow({ items, reader, subagentThreadId, preview }: { items: T3ActivityItem[]; reader: ActivityReader; subagentThreadId?: string; preview: boolean }) {
  const [open, setOpen] = useState(false);
  const running = items.some(isLiveItem);
  const failed = items.filter(isFailedItem).length;
  const Icon = RUN_ICONS[toolRunKind(items)];
  return (
    <li className="px-1">
      <button type="button" className={FOLD_LINE} aria-expanded={open} onClick={() => setOpen(!open)}>
        <Icon className={cn('size-3.5 shrink-0', failed && 'text-danger-strong/60')} aria-hidden />
        <span className="min-w-0 truncate">{toolRunSummary(items)}</span>
        {failed ? <span className="shrink-0 text-2xs text-danger-strong">· {failed} failed</span> : null}
        {running ? <StatusBadge meta={{ label: 'Running', tone: 'info' }} size="sm" dot /> : null}
        <FoldChevron open={open} />
      </button>
      {open ? (
        <ol className="mt-1.5 space-y-1.5" aria-label="Commands and tool calls">
          {items.map((item) => (
            <ActivityItemRow key={item.position} item={item} reader={reader} subagentThreadId={subagentThreadId} preview={preview} thread />
          ))}
        </ol>
      ) : null}
    </li>
  );
}

/** SF-T-222: Summarize thread's answer, in the thread where it was asked: only this person sees it, and it isn't saved on the task (the AI call log keeps it, as every AI call). */
function ThreadSummaryRow({ summary, onDismiss }: { summary: RunThreadSummary; onDismiss: () => void }) {
  return (
    <li className="space-y-1 rounded-lg bg-ai-soft px-3 py-2" aria-label="Thread summary">
      <div className="flex items-center gap-1.5 text-3xs font-medium tracking-wide text-ai-strong uppercase">
        <Sparkles className="size-3" aria-hidden />
        <span>Summary</span>
        <span className="font-normal tracking-normal normal-case">· only you see it here; not saved on the task</span>
        <span className="ml-auto font-normal tracking-normal normal-case tabular-nums">{formatRelative(summary.made_at)}</span>
        <Button type="button" variant="ghost" size="icon-xs" className="-my-1 size-5 text-ai-strong" aria-label="Dismiss the summary" onClick={onDismiss}>
          <X />
        </Button>
      </div>
      <p className="text-sm">{summary.summary}</p>
    </li>
  );
}
